import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Monitor,
  ChevronDown,
  FlaskConical,
  ShieldAlert,
  History,
  Keyboard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LegalGateModal, hasAcceptedLegal } from "@/components/legal/LegalGateModal";
import { useAuth } from "@/core/auth/auth";

const SUPABASE_BASE = "https://xymjnzqmpmlczzsioexl.supabase.co/storage/v1/object/public/downloads";
const STABLE_MANIFEST = `${SUPABASE_BASE}/desktop/desktop_versions.json`;
const WIP_MANIFEST = `${SUPABASE_BASE}/desktop/wip_versions.json`;
// RadiologyLive Mini — the hotkey companion. Published on its own lane by the
// radiologylive-desktop CI, so it never collides with LiveSpeak Desktop's manifest.
const MINI_MANIFEST = `${SUPABASE_BASE}/desktop/mini_versions.json`;
// macOS (Apple Silicon) Mini — its own manifest, same shape, written by the separate
// macOS CI lane, so the Windows CI's rewrite of mini_versions.json never drops it.
const MINI_MAC_MANIFEST = `${SUPABASE_BASE}/desktop/mini_mac_versions.json`;

interface StableVersion {
  version: string;
  url: string;
  date: string;
}

interface WipBuild {
  version: string;
  label: string;
  sha: string;
  filename: string;
  url: string;
  date: string;
  createdAt?: string;
}

function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na !== nb) return nb - na;
  }
  return 0;
}

function useStableVersions(count: number = 5): { versions: StableVersion[]; loading: boolean } {
  const [versions, setVersions] = useState<StableVersion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(STABLE_MANIFEST)
      .then((r) => r.json())
      .then((manifest: { versions?: StableVersion[] }) => {
        const parsed = (manifest.versions ?? [])
          .filter((v) => v?.version && v?.url)
          .sort((a, b) => compareVersions(a.version, b.version));
        setVersions(parsed.slice(0, count));
        setLoading(false);
      })
      .catch(() => {
        setVersions([
          {
            version: "0.5.5",
            url: `${SUPABASE_BASE}/desktop/RadiologyLive_0.5.5_x64-setup.exe`,
            date: "Feb 2026",
          },
        ]);
        setLoading(false);
      });
  }, [count]);

  return { versions, loading };
}

// Mirrors useStableVersions but against the Mini lane. No hardcoded fallback:
// if the manifest 404s (no Mini build published yet), the section simply hides.
function useMiniVersions(
  manifestUrl: string,
  count: number = 5,
): { versions: StableVersion[]; loading: boolean } {
  const [versions, setVersions] = useState<StableVersion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(manifestUrl, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { versions: [] }))
      .then((manifest: { versions?: StableVersion[] }) => {
        const parsed = (manifest.versions ?? [])
          .filter((v) => v?.version && v?.url)
          .sort((a, b) => compareVersions(a.version, b.version));
        setVersions(parsed.slice(0, count));
        setLoading(false);
      })
      .catch(() => {
        setVersions([]);
        setLoading(false);
      });
  }, [manifestUrl, count]);

  return { versions, loading };
}

function useWipBuilds(count: number = 5): { builds: WipBuild[]; loading: boolean } {
  const [builds, setBuilds] = useState<WipBuild[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(WIP_MANIFEST, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { builds: [] }))
      .then((manifest: { builds?: WipBuild[] }) => {
        const sorted = (manifest.builds ?? [])
          .filter((b) => b?.url && b?.label && b?.sha)
          .sort((a, b) => {
            if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
            return 0;
          });
        setBuilds(sorted.slice(0, count));
        setLoading(false);
      })
      .catch(() => {
        setBuilds([]);
        setLoading(false);
      });
  }, [count]);

  return { builds, loading };
}

export function PortalDownloads() {
  const { user } = useAuth();
  const { versions, loading: stableLoading } = useStableVersions(5);
  const { versions: miniVersions, loading: miniLoading } = useMiniVersions(MINI_MANIFEST, 5);
  const { versions: miniMacVersions, loading: miniMacLoading } = useMiniVersions(
    MINI_MAC_MANIFEST,
    5,
  );
  const { builds: wipBuilds, loading: wipLoading } = useWipBuilds(5);
  const latestVersion = versions[0];
  const latestMini = miniVersions[0];
  const latestMiniMac = miniMacVersions[0];
  const olderVersions = versions.slice(1);
  const accepted = useMemo(() => hasAcceptedLegal(user?.id), [user?.id]);

  const [showOlder, setShowOlder] = useState(false);
  const [showAllWip, setShowAllWip] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const requestDownload = (url: string) => {
    if (accepted) {
      window.location.assign(url);
      return;
    }
    setPendingUrl(url);
    setGateOpen(true);
  };

  const onGateAccepted = () => {
    if (!pendingUrl) return;
    window.location.assign(pendingUrl);
    setPendingUrl(null);
  };

  const primaryWip = wipBuilds[0];
  const moreWip = showAllWip ? wipBuilds.slice(1) : [];

  return (
    <div className="space-y-6">
      <LegalGateModal open={gateOpen} onOpenChange={setGateOpen} onAccepted={onGateAccepted} />

      <header>
        <h1 className="text-xl font-semibold">Downloads</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Install or update LiveSpeak Desktop for native dictation and paste workflows.
        </p>
      </header>

      {/* ─── Stable release ─── */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">Stable</h2>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Production
          </span>
        </div>

        {stableLoading ? (
          <div className="rounded-xl border border-border/50 p-4 text-sm text-muted-foreground">
            Loading versions…
          </div>
        ) : latestVersion ? (
          <button
            type="button"
            onClick={() => requestDownload(latestVersion.url)}
            className="w-full rounded-xl border border-border/50 bg-card/50 p-5 text-left transition-all hover:border-primary/50 hover:bg-accent/20"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Monitor className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold">LiveSpeak Desktop</div>
                <div className="text-sm text-muted-foreground">
                  v{latestVersion.version} · Windows x64 · {latestVersion.date}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Latest stable build for production workflows.
                </div>
              </div>
              <div className="shrink-0">
                <Button size="sm" className="gap-2" type="button">
                  <Download className="h-4 w-4" /> Download
                </Button>
              </div>
            </div>
          </button>
        ) : (
          <div className="rounded-xl border border-border/50 p-4 text-sm text-muted-foreground">
            No desktop versions available.
          </div>
        )}
      </section>

      {/* ─── RadiologyLive Mini (hotkey companion) ─── */}
      {!miniLoading && !miniMacLoading && (latestMini || latestMiniMac) && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">RadiologyLive Mini</h2>
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Companion
            </span>
          </div>

          {latestMini && (
            <button
              type="button"
              onClick={() => requestDownload(latestMini.url)}
              className="w-full rounded-xl border border-border/50 bg-card/50 p-5 text-left transition-all hover:border-primary/50 hover:bg-accent/20"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Keyboard className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold">RadiologyLive Mini</div>
                  <div className="text-sm text-muted-foreground">
                    v{latestMini.version} · Windows x64 · {latestMini.date}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Hotkey companion — highlight a finding, press F10, Liv answers. Runs alongside
                    LiveSpeak Desktop on the same machine.
                  </div>
                </div>
                <div className="shrink-0">
                  <Button size="sm" className="gap-2" type="button">
                    <Download className="h-4 w-4" /> Download
                  </Button>
                </div>
              </div>
            </button>
          )}
          {latestMiniMac && (
            <button
              type="button"
              onClick={() => requestDownload(latestMiniMac.url)}
              className="w-full rounded-xl border border-border/50 bg-card/50 p-5 text-left transition-all hover:border-primary/50 hover:bg-accent/20"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Keyboard className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold">RadiologyLive Mini</div>
                  <div className="text-sm text-muted-foreground">
                    v{latestMiniMac.version} · macOS · Apple Silicon · {latestMiniMac.date}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Hotkey companion for Mac (M1 or newer, macOS 11+). Preview build, not yet
                    notarized: if macOS blocks the first launch, open System Settings › Privacy &
                    Security and choose Open Anyway.
                  </div>
                </div>
                <div className="shrink-0">
                  <Button size="sm" className="gap-2" type="button">
                    <Download className="h-4 w-4" /> Download
                  </Button>
                </div>
              </div>
            </button>
          )}
        </section>
      )}

      {/* ─── Preview builds (WIP) ─── */}
      {!wipLoading && wipBuilds.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Preview</h2>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
              <FlaskConical className="h-3 w-3" />
              WIP · not for production
            </span>
          </div>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.03] p-4 space-y-3">
            <div className="flex items-start gap-2 text-xs text-amber-500/90">
              <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <p>
                These builds are for testing unreleased features on opt-in devices. They install to
                a separate path so the auto-updater will not promote them over your stable install.
              </p>
            </div>

            {primaryWip && (
              <button
                type="button"
                onClick={() => requestDownload(primaryWip.url)}
                className="w-full rounded-lg border border-amber-500/25 bg-background/40 p-4 text-left transition-all hover:border-amber-400/60 hover:bg-amber-500/[0.06]"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                    <FlaskConical className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-semibold">{primaryWip.label}</span>
                      <span className="text-xs text-muted-foreground">
                        v{primaryWip.version} · {primaryWip.sha}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Windows x64 · {primaryWip.date}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <Button size="sm" variant="outline" className="gap-2 border-amber-500/40 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400" type="button">
                      <Download className="h-4 w-4" /> Download
                    </Button>
                  </div>
                </div>
              </button>
            )}

            {wipBuilds.length > 1 && (
              <div>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  onClick={() => setShowAllWip((v) => !v)}
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAllWip ? "rotate-180" : ""}`} />
                  Earlier preview builds
                </button>
                {showAllWip && (
                  <div className="mt-2 space-y-1.5">
                    {moreWip.map((b) => (
                      <button
                        key={b.filename}
                        type="button"
                        onClick={() => requestDownload(b.url)}
                        className="w-full rounded-md border border-border/40 bg-background/30 px-3 py-2 flex items-center justify-between hover:border-amber-500/30 hover:bg-amber-500/[0.04]"
                      >
                        <div className="text-left min-w-0">
                          <div className="text-sm font-medium truncate">
                            {b.label} <span className="text-muted-foreground font-normal">· {b.sha}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            v{b.version} · {b.date}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0 ml-2">Windows x64</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Archive ─── */}
      {olderVersions.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <History className="h-3.5 w-3.5 text-muted-foreground" />
            Previous versions
          </h2>

          <div className="rounded-xl border border-border/40 p-3">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              onClick={() => setShowOlder((v) => !v)}
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showOlder ? "rotate-180" : ""}`} />
              {olderVersions.length} earlier stable release{olderVersions.length === 1 ? "" : "s"}
            </button>
            {showOlder && (
              <div className="mt-2 space-y-1.5">
                {olderVersions.map((v) => (
                  <button
                    key={v.version}
                    type="button"
                    onClick={() => requestDownload(v.url)}
                    className="w-full rounded-md border border-border/40 px-3 py-2 flex items-center justify-between hover:bg-accent/30"
                  >
                    <div className="text-left">
                      <div className="text-sm font-medium">v{v.version}</div>
                      <div className="text-xs text-muted-foreground">{v.date}</div>
                    </div>
                    <span className="text-xs text-muted-foreground">Windows x64</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
